<template>
  <v-col
    :class="`report-table pt-0 ${showCurrentSavedFilters ? 'summaries-expanded px-2' : 'px-0'}`"
  >
    <v-row v-if="isReady">
      <v-col>
        <v-row>
          <v-col class="pb-0">
            <v-card class="elevation-0">
              <v-card-text class="px-2 py-0">
                <v-col>
                  <v-row class="justify-end">
                    <v-col>
                      <v-row>
                        <v-col
                          v-if="showFilterBar && !hideToolbarButtons"
                          class="col-auto"
                        >
                          <filter-bar />
                        </v-col>

                        <v-col
                          v-if="showFilterBar && !hideToolbarButtons"
                          class="col-auto"
                        >
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click="columnDrawerOpen = true"
                          >
                            <font-awesome-icon :icon="['fas', 'sliders']" />
                            <span class="pl-1">Columns</span>
                          </v-btn>
                        </v-col>

                        <v-col
                          v-if="showItemBar && !hideToolbarButtons"
                          :class="showItemBar ? 'col-auto text-xs-center' : 'd-none'"
                        >
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click.stop="createItem(undefined)"
                          >
                            <font-awesome-icon :icon="['fas', 'plus-square']" />
                            <span class="pl-1">Add Item</span>
                          </v-btn>
                        </v-col>

                        <!-- Single-group label: replaces the standalone header row -->
                        <v-col
                          v-if="isSingleUngroupedView && singleGroupLabel"
                          class="col-auto d-flex align-center pl-2"
                        >
                          <span class="subtitle-2 font-weight-medium">
                            {{ singleGroupLabel }}
                          </span>
                        </v-col>

                        <v-spacer />

                        <v-col
                          v-if="showFilterBar"
                          class="col-auto"
                        >
                          <table-pagination
                            :show-filter-bar="showFilterBar"
                            :current-page="syncPage"
                            :total-pages="syncPageCount"
                            :items-per-page="syncItemsPerPage"
                            :items-per-page-options="itemsPerPageOptions"
                            :current-row-count="currentRowCount"
                            @page-left="shiftPageLeft"
                            @page-right="shiftPageRight"
                            @update:items-per-page="syncItemsPerPage = $event"
                          />
                        </v-col>

                        <v-col
                          v-if="showExportBar && !hideToolbarButtons"
                          class="col-auto text-center"
                        >
                          <v-menu
                            :disabled="!showExportBar"
                            min-width="90"
                            offset-y
                            nudge-bottom="6"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                color="primary"
                                small
                                v-bind="attrs"
                                v-on="on"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'circle-down']"
                                  size="sm"
                                />

                                <span class="pl-1">Export</span>
                              </v-btn>
                            </template>

                            <v-list>
                              <v-list-item
                                v-for="item in exportOptions"
                                :key="item.key"
                                :disabled="!item.active"
                                @click="item.callback"
                              >
                                <v-list-item-title>
                                  <v-col>
                                    <v-row v-if="item.key !== 'xlsx'">
                                      {{ item.text }}
                                    </v-row>

                                    <v-row v-else>
                                      <export-xlsx
                                        :item="item"
                                        :show-export-bar="showExportBar"
                                      />
                                    </v-row>
                                  </v-col>
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row
          v-for="group of paginatedGroups"
          :id="`group-${group.key || group.groupName}`"
          :key="group.groupName"
          :class="`table-group rounded-borders`"
        >
          <v-col
            v-if="!group.isPartOfGroup || group.totalCount > 0"
            :class="`${group.isPartOfGroup ? 'rounded-borders cursor-pointer px-6' : 'px-0 pt-0 table-group-surface'}`"
          >
            <v-row
              v-if="!isSingleUngroupedView || group.isPartOfGroup"
              class="row--dense subtitle-2 group-header-row"
              :class="{ 'group-header-sticky': group.isPartOfGroup, 'mt-2': group.isPartOfGroup }"
              role="button"
              :aria-label="`Toggle group ${(group.labels && group.labels[0]) || group.groupName || 'Items'}`"
              :aria-controls="`group-table-${group.key || group.groupName}`"
              :aria-expanded="showRows.indexOf(group.groupName) !== -1 ? 'true' : 'false'"
              :tabindex="group.isPartOfGroup ? 0 : -1"
              @click="toggleRows(group.groupName)"
              @keydown.enter.prevent="toggleRows(group.groupName)"
              @keydown.space.prevent="toggleRows(group.groupName)"
            >
              <v-col
                v-if="group.groupName"
                :class="`d-inline-flex flex-grow col-10 align-center py-3 group-header-title-col ${group.isPartOfGroup ? '' : 'pl-1'}`"
              >
                <span
                  v-if="group.isPartOfGroup"
                  class="mr-3 group-header-chevron-wrap"
                >
                  <font-awesome-icon
                    :icon="['fas', 'chevron-down']"
                    size="sm"
                    :class="`group-header-chevron transition-transform ${showRows.indexOf(group.groupName) !== -1 ? '' : 'rotate-minus-90'}`"
                  />
                </span>

                <span class="font-weight-bold primary--text">
                  {{ (group.labels && group.labels[0]) || group.groupName || 'Items' }}
                </span>

                <span
                  v-if="group.labels && group.labels.length > 1"
                  class="font-weight-regular pl-3"
                >
                  {{ group.labels.slice(1).join(' :: ') }}
                </span>
              </v-col>

              <v-spacer v-if="!isProgrammaticRoute" />

              <v-col
                v-if="group.groupName && !isProgrammaticRoute"
                :class="`d-inline-flex align-center justify-end coolgray--text text-right caption col-auto group-header-count-col ${group.isPartOfGroup ? '' : 'pt-0'}`"
              >
                <span class="group-header-count-label">
                  <span class="font-weight-medium">{{ formatNumber(group.totalCount) }}</span>
                  <span class="font-weight-light pl-1 d-none d-md-inline">{{ group.totalCount === 1 ? 'Result' : 'Results' }}</span>
                </span>
              </v-col>

              <v-col
                v-if="isProgrammaticRoute"
                :class="`align-center justify-end coolgray--text caption ${group.isPartOfGroup ? '' : 'pt-0'}`"
              >
                <v-row class="align-center px-4">
                  <v-col
                    v-for="(summary, summaryIndex) in group.summaries"
                    :key="summaryIndex"
                    :class="`col-auto coolgray--text justify-start my-0`"
                    :title="summary.title"
                    :style="`min-width: ${summary.widthPx};`"
                  >
                    <span class="font-weight-bold">
                      {{ summary.value }}
                    </span>

                    <span class="align-center font-weight-regular pl-1">
                      {{ summary.title }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-data-table
              v-if="!group.isPartOfGroup || showRows.indexOf(group.groupName) !== -1"
              :id="`group-table-${group.key || group.groupName}`"
              cell-class="font-weight-regular"
              dense
              fixed-header
              :height="tableHeight"
              :custom-sort="setTableSorting"
              :footer-props="tableFooterProps"
              :headers="effectiveHeaders"
              hide-default-header
              hide-default-footer
              :items="group.paginatedItems || group.items || []"
              :items-per-page="syncItemsPerPage"
              :loading="(group.totalCount || group.items.length) > 0 && (applyingFilter || resettingFilter)"
              multi-sort
              :page.sync="syncPage"
              :sort-by.sync="sortedByKeys"
              :sort-desc.sync="sortedByDirections"
              :class="`cursor-context-menu scroller tbody ${group.isPartOfGroup ? 'is-part-of-group' : ''}`"
              @page-count="syncPageCount = $event"
              @scroll="calculatePixelsFromTableTop"
            >
              <template v-slot:header="{ props: { headers: tableHeaders } }">
                <thead
                  ref="tableHeaderRow"
                  :style="`position: sticky; top: ${pixelsFromTableTop}; z-index: 8;`"
                  class="table-header-surface"
                  @contextmenu="toggleColumnMenu($event)"
                  @dblclick="toggleColumnMenu($event)"
                >
                  <tr>
                    <!-- Select All Checkbox Header -->
                    <th
                      v-if="showSelect"
                      class="select-checkbox-header"
                      style="width: 48px; min-width: 48px; max-width: 48px;"
                    >
                      <Checkbox
                        :input-value="allSelected"
                        :indeterminate="someSelected"
                        color="primary"
                        @change="toggleSelectAll"
                      />
                    </th>
                    <th
                      v-for="(header, headerIndex) in tableHeaders"
                      :key="header.key || headerIndex"
                      :class="[
                        `coolgray--text font-weight-semibold column-header ${header.classes}`,
                        { 'column-drop-before': columnDropIndex === headerIndex && columnDragIndex !== null && columnDragIndex > headerIndex },
                        { 'column-drop-after': columnDropIndex === headerIndex && columnDragIndex !== null && columnDragIndex < headerIndex },
                        { 'column-sticky-surface': header.classes && header.classes.includes('column-sticky') },
                      ]"
                      :style="header.styles"
                      :title="header.tooltip || header.text"
                      @dragover.prevent="onColumnDragOver($event, headerIndex)"
                      @drop="onColumnDrop($event, headerIndex)"
                    >
                      <div class="column-header-content">
                        <button
                          type="button"
                          class="column-drag-handle"
                          :title="`Reorder ${header.text}`"
                          draggable="true"
                          @dragstart="onColumnDragStart($event, headerIndex)"
                          @dragend="onColumnDragEnd"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'grip-vertical']"
                            size="xs"
                            class="midgray--text"
                          />
                        </button>

                        <div
                          class="column-header-main"
                          :style="`width: ${header.widthPx};`"
                        >
                          <!-- Column Header Label -->
                          <span
                            class="column-header-label text-truncate"
                          >
                            {{ header.text }}
                          </span>

                          <!-- Sort Control Button -->
                          <button
                            type="button"
                            :class="`sort-chip cursor-pointer ${header.sortIndex !== -1 ? 'sort-chip-active' : 'sort-chip-idle'}`"
                            :title="getSortControlTitle(header)"
                            @click="updateSorting(header)"
                          >
                            <span class="sort-chip-content">
                              <font-awesome-icon
                                :icon="getSortControlIcon(header)"
                                size="xs"
                              />
                              <span
                                v-if="header.sortIndex !== -1"
                                class="text-smaller"
                              >
                                {{ header.sortIndex }}
                              </span>
                              <span
                                v-else
                                class="text-smaller sort-chip-label"
                              >
                                Sort
                              </span>
                            </span>
                          </button>
                        </div>

                        <!-- Column Resizer -->
                        <column-resizer
                          :column-index="headerIndex"
                          :column-width="normalizeColumnWidth(header.width || header.widthPx || (header.styles && header.styles.width))"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
              </template>

              <template v-slot:item="{ headers: rowHeaders, item, index }">
                <tr
                  :class="[
                    'table-row-surface',
                    { 'table-row-context-active': contextMenuItemIndex === index },
                    { 'cursor-pointer': $listeners['click:row'] }
                  ]"
                  @contextmenu="toggleContextMenu($event, item, index)"
                  @dblclick="toggleContextMenu($event, item, index)"
                  @click="resetContextMenu(null); onRowClick(item)"
                >
                  <!-- Row Selection Checkbox -->
                  <td
                    v-if="showSelect"
                    class="select-checkbox-cell"
                    style="width: 48px; min-width: 48px; max-width: 48px;"
                    @click.stop
                  >
                    <Checkbox
                      :input-value="isItemSelected(item)"
                      color="primary"
                      @change="toggleItemSelection(item)"
                    />
                  </td>
                  <td
                    v-for="(header, hIndex) in rowHeaders"
                    :key="header.key || hIndex"
                    :class="[header.classes, { 'column-sticky-surface': header.classes && header.classes.includes('column-sticky') }]"
                    :style="header.styles"
                  >
                    <v-row
                      no-gutters
                      class="align-center"
                      :style="`width: ${header.widthPx};`"
                    >
                      <!-- Custom column slot support -->
                      <slot
                        v-if="$scopedSlots[`item.${header.key || header.value}`]"
                        :name="`item.${header.key || header.value}`"
                        :item="item"
                        :header="header"
                        :value="item[header.key || header.value]"
                      />
                      <!-- Default column rendering -->
                      <table-column
                        v-else
                        :header="header"
                        :item="item"
                      />
                    </v-row>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Group-level "Show More" pagination -->
            <v-row
              v-if="group.hasMore && (!group.isPartOfGroup || showRows.indexOf(group.groupName) !== -1)"
              class="justify-center my-2"
            >
              <v-col class="text-center">
                <v-btn
                  outlined
                  small
                  class="text-none"
                  @click.stop="loadMoreForGroup(group.groupName)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'chevron-down']"
                    size="sm"
                    class="mr-2"
                  />
                  Show more ({{ formatNumber(group.paginatedItems.length) }} of {{ formatNumber(group.totalCount) }})
                </v-btn>
              </v-col>
            </v-row>

            <!-- Empty state for filtered groups -->
            <v-card
              v-if="(!group.isPartOfGroup || showRows.indexOf(group.groupName) !== -1) && group.isEmpty"
              flat
              class="pa-6 text-center"
            >
              <v-row justify="center">
                <v-col cols="12">
                  <font-awesome-icon
                    :icon="['fas', 'filter-circle-xmark']"
                    size="3x"
                    class="coolgray--text mb-4"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="text-h6 coolgray--text mb-2">
                    No items match current filters
                  </div>
                  <div class="text-body-2 coolgray--text">
                    This group had {{ group.originalCount }} {{ group.originalCount === 1 ? 'item' : 'items' }} before filters were applied
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <v-menu
              v-model="contextMenu"
              absolute
              offset-y
              :position-x="contextMenuX"
              :position-y="contextMenuY"
              transition="scale-transition"
              width="300px"
            >
              <v-card>
                <v-card-actions>
                  <v-list dense>
                    <v-list-item
                      v-for="(action, actionIndex) in actions"
                      :key="actionIndex"
                      @click="fireActionEvent(action, contextMenuItem)"
                    >
                      <v-list-item-icon
                        v-if="action.icon"
                        class="mr-2 my-auto"
                      >
                        <font-awesome-icon
                          :icon="action.icon"
                          size="sm"
                        />
                      </v-list-item-icon>
                      <v-list-item-content class="body-2">
                        {{ action.name }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-actions>
              </v-card>
            </v-menu>

            <!-- Right-click column menu for power users -->
            <v-menu
              v-model="columnMenu"
              absolute
              max-height="300"
              min-width="300"
              offset-y
              :close-on-content-click="false"
              :position-x="columnMenuX"
              :position-y="columnMenuY"
              bottom
              left
              transition="scale-transition"
              width="300px"
            >
              <column-picker
                :current-required-columns="currentRequiredColumns"
                :report-key="reportKey"
                :table-columns="tableColumns"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col class="pt-0">
            <v-card class="elevation-0">
              <v-card-text class="px-2 py-0">
                <v-col>
                  <v-row class="align-center">
                    <!-- Left anchor: jump navigation -->
                    <v-col
                      cols="12"
                      sm="auto"
                      class="pb-1 pb-sm-0"
                    >
                      <jump-to-group
                        :groups="paginatedGroups"
                        @scroll-to-group="scrollToGroup"
                      />
                    </v-col>

                    <!-- Injected slot: contextual actions (e.g. bulk-edit toolbar) -->
                    <v-col
                      cols="12"
                      sm="auto"
                      class="pb-1 pb-sm-0"
                    >
                      <slot name="bottom-actions" />
                    </v-col>

                    <v-spacer />

                    <!-- Right anchor: pagination -->
                    <v-col
                      cols="12"
                      sm="auto"
                      class="d-flex justify-sm-end"
                    >
                      <table-pagination
                        :show-filter-bar="showFilterBar"
                        :current-page="syncPage"
                        :total-pages="syncPageCount"
                        :items-per-page="syncItemsPerPage"
                        :items-per-page-options="itemsPerPageOptions"
                        :current-row-count="currentRowCount"
                        @page-left="shiftPageLeft"
                        @page-right="shiftPageRight"
                        @update:items-per-page="syncItemsPerPage = $event"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Column Drawer -->
    <column-drawer
      :drawer-open="columnDrawerOpen"
      :current-required-columns="currentRequiredColumns"
      :report-key="reportKey"
      :table-columns="tableColumns"
      @close="columnDrawerOpen = false"
    />
  </v-col>
</template>

<script>
import _ from 'underscore'
import { createNamespacedHelpers } from 'vuex'

import { fireanalytics } from 'src/store/firebase'

import dates from 'src/utils/dates'
import format from 'src/utils/format'
import sort from 'src/utils/sort'

import ColumnDrawer from './ColumnDrawer'
import ColumnPicker from './ColumnPicker'
import ColumnResizer from './ColumnResizer'
import TableColumn from './TableColumn'
import TablePagination from './TablePagination'
import Checkbox from '@/tools/components/Checkbox'
import JumpToGroup from '@/tools/components/filter/JumpToGroup.vue'
import columnUtils from '@/tools/store/utils/columns'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutsGetters, mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapActions, mapGetters, mapState } = createNamespacedHelpers('tools')

const { direction: SortDirection, label: SortLabel } = sort

export default {
  name: 'DataTable',
  components: {
    // Dynamic import caused a split-second lag.
    Checkbox,
    ColumnDrawer,
    ColumnPicker,
    ColumnResizer,
    JumpToGroup,
    TableColumn,
    TablePagination,

    ExportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    FilterBar: () => import('src/tools/components/filter/FilterBar'),
  },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    dataGroups: {
      type: Array,
      default: null,
    },
    /**
     * Custom headers to use instead of store-based visibleColumns.
     * Each header should have: text, value/key, sortable (optional), align (optional)
     * When provided, enables custom column rendering via slots.
     */
    headers: {
      type: Array,
      default: null,
    },
    initialItemsPerPage: {
      type: Number,
      default: null,
    },
    hideToolbarButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * Property name to use as unique identifier for items.
     * Required when using showSelect.
     */
    itemKey: {
      type: String,
      default: 'id',
    },
    /**
     * Show selection checkboxes for rows.
     */
    showSelect: {
      type: Boolean,
      default: false,
    },
    /**
     * Array of selected items (for v-model binding).
     */
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      columnMenu: false,
      columnMenuX: 0,
      columnMenuY: 0,
      columnDrawerOpen: false,
      columnPickerDialog: false,
      contextMenu: false,
      contextMenuItem: null,
      contextMenuItemIndex: null,
      contextMenuX: 0,
      contextMenuY: 0,
      format,
      observer: null,
      SortDirection,
      tableOptions: {},

      // Group-level pagination: page map tracks current page per group key.
      groupPageSize: 50,
      groupPageMap: {},

      // Column header drag-to-reorder state.
      columnDragIndex: null,
      columnDropIndex: null,

      // Component-level column order – array of column keys.
      // When non-empty, effectiveHeaders uses this order instead of the store's.
      // Decouples reorder from global Vuex state (Vue 3 migration–ready).
      localColumnOrder: [],

      // Ghost element for column drag image.
      dragGhost: null,
    }
  },
  computed: {
    ...mapAuthGetters([
      'inDarkMode',
      'isInternalWriter',
    ]),

    ...mapGetters([
      'columnsByKey',
      'currentColumns',
      'currentGroupByProperties',
      'currentGroups',
      'currentlySorting',
      'currentNumberOfAppliedFilters',
      'currentSorted',
      'currentSorting',
      'currentSummary',
      'currentViewId',
      'frozenColumns',
      'isProgrammaticRoute',
      'keyField',
      'reportColumns',
      'reportKey',
      'showCurrentSavedFilters',
      'sizeField',
      'sortByDirections',
      'sortByKeys',
      'storedItems',
      'tableColumns',
      'visibleColumns',
    ]),
    ...mapState({
      applyingFilter: (state) => state.applyingFilter,
      displaySavedFilters: (state) => state.displaySavedFilters,
      dragging: (state) => state.dragging,
      hasClickedToggler: (state) => state.hasClickedToggler,
      internalRoutes: (state) => state.internalRoutes,
      isTogglingTables: (state) => state.isTogglingTables,
      itemsPerPage: (state) => state.itemsPerPage,
      itemsPerPageOptions: (state) => state.itemsPerPageOptions,
      page: (state) => state.page,
      pageCount: (state) => state.pageCount,
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      resettingFilter: (state) => state.resettingFilter,
      showRows: (state) => state.showRows,
      subId: (state) => state.subId,

      requiredColumns: (state) => state.requiredColumns,
      sortClassIndicatorMap: (state) => state.sortClassIndicatorMap,
      sortClassTextMap: (state) => state.sortClassTextMap,
      sortIcons: (state) => state.sortIcons,
      sortingByKeys: (state) => state.sortingByKeys,
      sortingByDirections: (state) => state.sortingByDirections,
      tableSortMap: (state) => state.tableSortMap,
    }),

    ...mapLayoutsGetters([
      'showDataTools',
      'showExportBar',
      'showFilterBar',
      'showLayoutBar',
    ]),

    ...mapLayoutState({
      darkMode: (state) => state.darkMode,
      icons: (state) => state.icons,
    }),

    isDarkTheme() {
      return this.darkMode === true || this.$vuetify?.theme?.dark === true
    },

    pixelsFromTableTop() {
      // Get the number of pixels from the top of the first row to the top of the first visible row.
      const { tableHeaderRow } = this.$refs
      const tableHeaderRowTop = tableHeaderRow?.getBoundingClientRect().top
      const tableHeaderRowHeight = tableHeaderRow?.clientHeight

      return tableHeaderRowTop + tableHeaderRowHeight
    },

    tableHeight() {
      // Return undefined to allow dynamic height
      // Max-height is controlled via CSS (.v-data-table__wrapper)
      // This provides better UX:
      // - Few rows: table is compact, no whitespace
      // - Many rows: table scrolls at max-height
      return undefined
    },

    /**
     * Returns custom headers prop if provided, otherwise falls back to store-based visibleColumns.
     * Normalizes header structure to ensure `key` property exists for slot lookup.
     */
    effectiveHeaders() {
      let cols
      if (this.headers && this.headers.length > 0) {
        // Normalize headers to ensure key property exists
        cols = this.headers.map((header) => ({
          ...header,
          key: header.key || header.value,
          value: header.value || header.key,
        }))
      } else {
        cols = this.visibleColumns
      }

      // Apply component-level column order when present.
      if (this.localColumnOrder.length > 0) {
        const colsByKey = {}
        cols.forEach((c) => { colsByKey[c.key] = c })
        const ordered = this.localColumnOrder
          .map((key) => colsByKey[key])
          .filter(Boolean)
        // Append any columns not yet in localColumnOrder (newly added).
        const orderedKeys = new Set(this.localColumnOrder)
        cols.forEach((c) => {
          if (!orderedKeys.has(c.key)) ordered.push(c)
        })
        return ordered
      }
      return cols
    },

    syncPage: {
      get() {
        return this.page
      },
      set(value) {
        this.$store.commit('tools/page', value)
      },
    },
    syncPageCount: {
      get() {
        return this.pageCount
      },
      set(value) {
        this.$store.commit('tools/pageCount', value)
      },
    },
    syncItemsPerPage: {
      get() {
        // Use prop value if provided, otherwise fall back to store value
        return this.initialItemsPerPage !== null ? this.initialItemsPerPage : this.itemsPerPage
      },
      set(value) {
        this.$store.commit('tools/itemsPerPage', value)
      },
    },

    showItemBar() {
      const { addIcon, internalRoutes, isInternalWriter, reportKey } = this
      const isAddRoute = () => addIcon && internalRoutes.includes(reportKey)

      return reportKey && isInternalWriter && isAddRoute()
    },

    sortedByDirections: {
      get() {
        return this.sortByDirections
      },
      set(newValue) {
        this.$store.commit('tools/sortingByDirections', newValue)
      },
    },
    sortedByKeys: {
      get() {
        return this.sortByKeys
      },
      set(newValue) {
        this.$store.commit('tools/sortingByKeys', newValue)
      },
    },

    currentRequiredColumns() {
      return this.requiredColumns[this.reportKey] || []
    },

    // Selection computed properties
    selectedItems: {
      get() {
        return this.value || []
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    allItems() {
      // Flatten all items from all groups
      return this.tableGroups.reduce((acc, group) => acc.concat(group.items || []), [])
    },
    allSelected() {
      if (!this.showSelect || this.allItems.length === 0) return false
      return this.allItems.every((item) => this.isItemSelected(item))
    },
    someSelected() {
      if (!this.showSelect || this.allItems.length === 0) return false
      const selectedCount = this.allItems.filter((item) => this.isItemSelected(item)).length
      return selectedCount > 0 && selectedCount < this.allItems.length
    },

    addPaddingToTable() {
      const { displaySavedFilters, reportKey } = this
      const isPacingReport = reportKey === 'pacing'

      return !isPacingReport || displaySavedFilters[reportKey]
    },

    tableGroups() {
      return this.dataGroups || this.currentGroups
    },

    /**
     * True when the table has exactly one flat (non-grouped) group.
     * Used to fold the group label into the toolbar row instead of
     * rendering a redundant standalone header row below the toolbar.
     */
    isSingleUngroupedView() {
      return this.paginatedGroups.length === 1 && !this.paginatedGroups[0].isPartOfGroup
    },

    singleGroupLabel() {
      if (!this.isSingleUngroupedView) return ''
      const group = this.paginatedGroups[0]
      return (group.labels && group.labels[0]) || group.groupName || ''
    },

    /**
     * Returns groups with paginated items and a hasMore flag.
     * Only rows up to the current page are rendered for each group,
     * keeping the DOM lightweight for large grouped datasets.
     */
    paginatedGroups() {
      return (this.tableGroups || []).map((group) => {
        const currentPage = this.groupPageMap[group.groupName] || 1
        const visibleCount = currentPage * this.groupPageSize
        const items = group.items || []
        const paginatedItems = items.slice(0, visibleCount)

        return {
          ...group,
          paginatedItems,
          totalCount: items.length,
          hasMore: visibleCount < items.length,
        }
      })
    },

    isReady() {
      return !!this.currentViewId
    },
    isReadyAndHasResults() {
      const { tableGroups, isReady } = this
      return isReady && !!tableGroups?.length
    },
    isReadyAndNoData() {
      const { tableGroups, isReady } = this

      return isReady && !tableGroups?.length
    },
    allGroupsAreEmpty() {
      const { tableGroups } = this
      const foundItems = tableGroups.find((x) => x.items.length > 0)

      return !foundItems
    },
    // Override currentRowCount when dataGroups is provided
    currentRowCount() {
      if (this.dataGroups) {
        // Calculate from tableGroups when custom dataGroups is provided
        return this.tableGroups.reduce((rowCount, currentGroup) => {
          rowCount += currentGroup.items.length
          return rowCount
        }, 0)
      }
      // Fall back to store's currentRowCount when no custom dataGroups
      return this.$store.getters['tools/currentRowCount']
    },
    tableFooterProps() {
      return {
        absolute: true,
        'items-per-page-options': this.itemsPerPageOptions,
      }
    },
    topPadding() {
      return this.currentGroupByProperties.length ? 'mt-1' : 'mt-0'
    },
    addIcon() {
      const { icons: { navigation: { add: addIcons } }, reportKey } = this

      return (!!reportKey && addIcons[reportKey]) || null
    },
    layouts() {
      const { tableGroups, showFilterBar, showLayoutBar } = this
      const isToggleGroupsActive = showFilterBar && !!tableGroups?.length && tableGroups[0]?.groupName !== null

      return [
        {
          active: isToggleGroupsActive,
          helpText: 'See Group & Filter to activate',
          key: 'groups',
          text: 'Toggle Groups',
          callback: this.toggleGroups,
        },
        {
          active: showLayoutBar,
          helpText: 'No saved filters to toggle',
          key: 'summaries',
          text: 'Toggle Saved Filters',
          callback: this.toggleSummaries,
        },
      ]
    },

    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },
  },
  watch: {
    // Reset group pagination and auto-expand groups whenever the underlying groups change
    // (new filter, new data, different grouping).
    tableGroups(newGroups) {
      this.resetGroupPagination()

      // Auto-expand all groups when grouping is applied via dataGroups prop
      if (this.dataGroups && newGroups && newGroups.length > 0) {
        const groupNames = newGroups
          .filter((g) => g.isPartOfGroup && g.groupName)
          .map((g) => g.groupName)

        if (groupNames.length > 0) {
          this.$store.commit('tools/showRows', groupNames)
        }
      }
    },
  },
  created() {
    // Initialize store with prop value if provided
    if (this.initialItemsPerPage !== null) {
      this.$store.commit('tools/itemsPerPage', this.initialItemsPerPage)
    }
  },
  methods: {
    ...mapActions(['keepDragging', 'startDragging', 'stopDragging', 'toggleRows']),

    // Selection methods
    isItemSelected(item) {
      if (!this.showSelect) return false
      const key = this.itemKey
      return this.selectedItems.some((selected) => selected[key] === item[key])
    },
    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all
        this.selectedItems = []
      } else {
        // Select all
        this.selectedItems = [...this.allItems]
      }
    },
    toggleItemSelection(item) {
      const key = this.itemKey
      const isSelected = this.isItemSelected(item)
      if (isSelected) {
        this.selectedItems = this.selectedItems.filter((s) => s[key] !== item[key])
      } else {
        this.selectedItems = [...this.selectedItems, item]
      }
    },
    onRowClick(item) {
      this.$emit('click:row', item)
    },
    scrollToGroup(groupName) {
      const element = document.getElementById(`group-${groupName}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    // Normalize widths from Vuetify slot headers (e.g., "150px") to numbers.
    normalizeColumnWidth(width) {
      if (typeof width === 'number') return width
      if (typeof width === 'string') {
        const parsed = Number.parseFloat(width.replace('px', '').trim())
        return Number.isFinite(parsed) ? parsed : 0
      }
      return 0
    },

    // Group pagination: load next batch for a specific group.
    loadMoreForGroup(groupName) {
      const currentPage = this.groupPageMap[groupName] || 1
      this.$set(this.groupPageMap, groupName, currentPage + 1)
    },
    resetGroupPagination() {
      this.groupPageMap = {}
    },

    // Column header drag-to-reorder handlers.
    onColumnDragStart(event, colIndex) {
      this.columnDragIndex = colIndex
      const { isDarkTheme } = this

      // Required for Firefox – drag won't initiate without setData.
      event.dataTransfer.setData('text/plain', String(colIndex))
      event.dataTransfer.effectAllowed = 'move'

      // Mark the source column for visual dimming.
      const th = event.target.closest('th')
      if (th) {
        th.classList.add('column-dragging-source')
      }

      // Build a ghost image from the header text for the drag cursor.
      const ghost = document.createElement('div')
      ghost.textContent = this.effectiveHeaders[colIndex]?.text || ''
      ghost.className = 'column-drag-ghost'
      Object.assign(ghost.style, {
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
        padding: '6px 12px',
        background: isDarkTheme ? '#333' : '#fff',
        color: isDarkTheme ? '#eee' : '#333',
        border: '1px solid #2196F3',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        zIndex: '9999',
        pointerEvents: 'none',
      })
      document.body.appendChild(ghost)
      this.dragGhost = ghost
      event.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2)

      // IMPORTANT: Defer pointer-events lockdown until after the browser
      // has captured the drag image and committed the drag session.
      // Applying pointer-events:none to the drag handle's ancestors
      // during dragstart can cause some browsers to abort the drag.
      requestAnimationFrame(() => {
        const thead = th?.closest('thead')
        if (thead) {
          thead.classList.add('column-reordering')
        }
      })
    },
    onColumnDragOver(event, colIndex) {
      // The event is already prevented via .prevent modifier on the template.
      if (this.columnDragIndex === null || this.columnDragIndex === colIndex) {
        this.columnDropIndex = null
        return
      }
      event.dataTransfer.dropEffect = 'move'
      this.columnDropIndex = colIndex
    },
    onColumnDrop(event, colIndex) {
      event.preventDefault()
      const fromIndex = this.columnDragIndex
      this.columnDragIndex = null
      this.columnDropIndex = null
      if (fromIndex === null || fromIndex === colIndex) return

      // Component-level reorder: update local column order directly.
      const currentHeaders = this.effectiveHeaders
      const order = currentHeaders.map((c) => c.key)
      const [moved] = order.splice(fromIndex, 1)
      order.splice(colIndex, 0, moved)
      this.localColumnOrder = order

      // Sync to store so ColumnDrawer reflects the new order.
      const { reportKey, tableColumns } = this
      if (reportKey && tableColumns) {
        const sorted = columnUtils.reorderColumnsByKeys(tableColumns, order)
        this.$store.commit('tools/columns', { [reportKey]: sorted })
      }
    },
    onColumnDragEnd() {
      this.columnDragIndex = null
      this.columnDropIndex = null

      // Clean up ghost element kept alive for the drag image.
      if (this.dragGhost && this.dragGhost.parentNode) {
        this.dragGhost.parentNode.removeChild(this.dragGhost)
      }
      this.dragGhost = null

      document.querySelectorAll('.column-dragging-source').forEach((el) => {
        el.classList.remove('column-dragging-source')
      })
      document.querySelectorAll('.column-reordering').forEach((el) => {
        el.classList.remove('column-reordering')
      })
    },

    toggleColumnMenu(event) {
      event.preventDefault()

      const newValue = !this.columnMenu

      if (newValue) {
        this.columnMenuX = event.clientX
        this.columnMenuY = event.clientY
        this.columnMenu = newValue
      } else {
        this.columnMenu = newValue
      }
    },
    resetContextMenu(newValue) {
      this.contextMenu = newValue
      this.contextMenuItemIndex = null
      this.contextMenuItem = null
    },
    toggleContextMenu(event, item, itemIndex) {
      event.preventDefault()

      const newValue = !this.contextMenu

      if (newValue) {
        this.contextMenuItem = item
        this.contextMenuItemIndex = itemIndex
        this.contextMenuX = event.clientX
        this.contextMenuY = event.clientY
        this.contextMenu = newValue
      } else {
        this.resetContextMenu(newValue)
      }
    },
    fireActionEvent(action, item) {
      if (typeof action.event === 'function') {
        action.event(item)
      } else {
        this.$store.dispatch(action.event, item)
      }
    },
    haveDataButNoResultsInGroup(group) {
      const { allGroupsAreEmpty, isReadyAndHasResults } = this

      if (allGroupsAreEmpty) return false

      return isReadyAndHasResults && group.items.length === 0
    },
    getClassNames(value, column) {
      const { formatAs = 'text' } = column
      const isNonTextType = ['boolean', 'number', 'percentage'].indexOf(formatAs) !== -1
      const formatClass = `column-${isNonTextType ? 'narrow' : formatAs}`
      const xAxisPadding = 'pl-0 pr-3'
      // const isFirstColumn = columnIndex === 0
      // const xAxisPadding = isFirstColumn ? 'px-3' : 'pl-0 pr-3'

      return `${xAxisPadding} ${formatClass} ${column.classes}`
    },
    formatNumber(value) {
      return format.number(value)
    },
    sortItems({ columnKey, columnDirection, columnFormat, items }) {
      const sortDsc = columnDirection === this.tableSortMap.DSC
      const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
      const isDateColumn = columnFormat === 'date'

      const valueGetter = (item) => {
        const getValue = () => {
          const value = !isDateColumn && item.normalized ? item.normalized[columnKey] : item[columnKey]
          const sortArrayValues = () => (sortDsc ? value.sort().reverse() : value.sort())
          return Array.isArray(value) ? sortArrayValues()[0] : value?.value || value
        }
        const getDate = (value) => dates.getUnixDate(value)
        return isDateColumn ? getDate(getValue()) : getValue()
      }

      return sorter(items, valueGetter)
    },
    addSortingOption(optionIndex, header) {
      const { ASC } = this.tableSortMap
      const addOption = () => {
        this.sortedByKeys.splice(this.sortedByKeys.length, 0, header.key)
        this.sortedByDirections.splice(this.sortedByDirections.length, 0, ASC)

        header.sortClassIndicator = `brightblue--text ${this.sortClassIndicatorMap[header.align]}`
        header.sortDsc = ASC
        header.sortIcon = this.sortIcons.ASC
        header.sortIndex = this.sortedByKeys.length

        fireanalytics.event('goal_completion', {
          action: 'click',
          category: 'Sort',
          description: 'Add Sort Option',
          label: `Add Sort Option "${header.key}" with Direction "${SortLabel[header.sortDsc]}" at Index ${header.sortIndex}`,
        })

        return true
      }

      return (optionIndex === -1 && addOption()) || false
    },
    deleteSortingOption(optionIndex, header) {
      const { ASC, DSC } = this.tableSortMap
      const removeOption = () => {
        fireanalytics.event('goal_completion', {
          action: 'click',
          category: 'Sort',
          description: 'Remove Sort Option',
          label: `Remove Sort Option "${header.key}" with Direction "${SortLabel[header.sortDsc]}" at Index ${header.sortIndex}`,
        })

        this.sortedByDirections.splice(optionIndex, 1)
        this.sortedByKeys.splice(optionIndex, 1)

        this.sortedByKeys.forEach((key, sortIndex) => {
          const foundHeader = this.columnsByKey[key]
          foundHeader.sortIndex = sortIndex + 1
        })

        // @TODO Avoid issue w/ sortClassIndicator's text color pre- vs post-click by abstracting the values.
        header.sortClassIndicator = `midgray--text ${this.sortClassIndicatorMap[header.align]}`
        header.sortDsc = ASC
        header.sortIcon = this.sortIcons.ASC
        header.sortIndex = -1

        return true
      }
      return (this.sortedByDirections[optionIndex] === DSC && removeOption()) || false
    },
    updateSortingOption(optionIndex, header) {
      const { ASC, DSC } = this.tableSortMap
      const currentDirection = this.sortedByDirections[optionIndex]
      const newDirection = currentDirection === DSC ? ASC : DSC
      const newSortIndex = optionIndex + 1

      fireanalytics.event('goal_completion', {
        action: 'click',
        category: 'Sort',
        description: 'Update Sort Option',
        label: `Update Sort Option "${header.columnName}" (${header.key}) from Direction "${SortLabel[header.sortDsc]}" to Direction "${newDirection}" at Index ${header.sortIndex}`,
      })

      header.sortClassIndicator = `brightblue--text ${this.sortClassIndicatorMap[header.align]}`
      header.sortDsc = newDirection
      header.sortIcon = this.sortIcons[newDirection]
      header.sortIndex = newSortIndex

      this.sortedByDirections.splice(optionIndex, 1, newDirection)

      return true
    },
    updateSorting(header) {
      const optionIndex = this.sortedByKeys.findIndex((key) => key === header.key)

      const changed = this.addSortingOption(optionIndex, header)
          || this.deleteSortingOption(optionIndex, header)
          || this.updateSortingOption(optionIndex, header)

      // Commit fresh array references so Vuetify detects the sort-by change
      if (changed) {
        this.sortedByKeys = [...this.sortedByKeys]
        this.sortedByDirections = [...this.sortedByDirections]
      }

      return changed
    },
    /**
     * @method getSortControlIcon
     * @param {Object} header - The header object for the column
     * @returns {Array} - The value for the FontAwesome component's icon property.
     */
    getSortControlIcon(header) {
      const isSorted = header.sortIndex !== -1

      if (!isSorted) {
        return ['fas', 'arrows-up-down']
      }

      // @see src/tools/components/filter/ToolsToolbar.vue#data
      return header.sortDsc === this.tableSortMap.DSC
        ? ['fas', 'arrow-down-short-wide']
        : ['fas', 'arrow-down-wide-short']
    },
    getSortControlTitle(header) {
      const isSorted = header.sortIndex !== -1

      if (!isSorted) {
        return `Sort by ${header.text}`
      }

      const directionLabel = header.sortDsc === this.tableSortMap.DSC
        ? 'descending'
        : 'ascending'

      return `Sorted ${directionLabel} (priority ${header.sortIndex}). Click to cycle sort.`
    },
    setTableSorting(unsortedItems) {
      const { columnsByKey } = this
      const sortItems = (items, columnKey, index) => this.sortItems({
        columnDirection: this.sortedByDirections[index],
        columnFormat: columnsByKey[columnKey]?.formatAs,
        columnKey,
        items,
      })

      return this.sortedByKeys.reduceRight(sortItems, unsortedItems)
    },

    shiftPageLeft() {
      // this.syncPage = Math.max(this.syncPage - 1, 1)
      if (this.syncPage > 1) {
        this.syncPage -= 1
      }
    },
    shiftPageRight() {
      // this.syncPage = Math.min(this.syncPage + 1, this.syncPageCount)
      if (this.syncPage < this.syncPageCount) {
        this.syncPage += 1
      }
    },

    calculatePixelsFromTableTop() {
      const { tableHeaderRow } = this.$refs
      const tableHeaderRowTop = tableHeaderRow?.getBoundingClientRect().top
      const tableHeaderRowHeight = tableHeaderRow?.clientHeight

      this.pixelsFromTableTop = tableHeaderRowTop + tableHeaderRowHeight
    },

    toggleSummaries() {
      this.$store.dispatch('layout/toggleSummaries')
    },

    createItem(value) {
      this.$store.dispatch('internal/createItem', value)
    },

    toggleGroups() {
      this.$store.dispatch('tools/toggleGroups')
    },

    exportAsCsv() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },
    exportAsXlsx() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

  },
}
</script>

<style lang="scss" scoped>
@import "src/styles/tables";

#header-thead {
  position: sticky;
  top: 0;
}

#header-tbody {
  position: absolute;
  top: 0;

  overflow: scroll;
  width: max-content;

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
#header-tbody::-webkit-scrollbar {
  display: none;
}

.transition-transform {
  transition: transform 0.2s ease-in-out;
}

.rotate-minus-90 {
  transform: rotate(-90deg);
}

.group-header-sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(243, 247, 251, 0.98) 100%);
  box-shadow: 0 10px 24px rgba(10, 35, 66, 0.06);
  border-bottom: 1px solid rgba(59, 91, 123, 0.16);
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-bottom-color: rgba(var(--v-primary-base), 0.24);
    box-shadow: 0 14px 30px rgba(10, 35, 66, 0.09);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--v-primary-base, #1976d2);
    outline-offset: -2px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: inherit;
    z-index: -1;
  }
}

.group-header-title-col {
  padding-left: 16px;
}

.group-header-chevron {
  font-size: 0.75rem;
}

.group-header-chevron-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(var(--v-primary-base), 0.1);
  color: var(--v-primary-base);
  flex-shrink: 0;
}

.group-header-count-col {
  padding-right: 14px;
}

.group-header-count-label {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(18, 48, 75, 0.06);
}

@media (max-width: 959px) {
  .group-header-title-col {
    padding-left: 12px;
  }

  .group-header-count-col {
    padding-right: 10px;
  }
}

// Dark mode support for sticky headers
.theme--dark .group-header-sticky {
  background: linear-gradient(180deg, rgba(44, 44, 44, 0.96) 0%, rgba(36, 36, 36, 0.98) 100%);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
  border-bottom-color: rgba(255, 255, 255, 0.08);

  &:hover {
    border-bottom-color: rgba(100, 181, 246, 0.35);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.35);
    transform: translateY(-1px);
  }
}

.theme--dark .group-header-count-label {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .group-header-chevron-wrap {
  background: rgba(100, 181, 246, 0.2);
  color: #90caf9;
}

// Dynamic table height: compact for few rows, scrollable for many
::v-deep .v-data-table__wrapper {
  max-height: calc(100vh - 212px);
  overflow-y: auto;
  overflow-x: auto;  // Enable horizontal scrolling for many columns
  width: 100%;

  // When content is shorter than max-height, table uses natural height
  // When content exceeds max-height, table becomes scrollable vertically
  // When content exceeds viewport width, table becomes scrollable horizontally
}

// During reorder drag: make th children transparent to pointer/drag events
// so the <th> itself receives all dragover/drop events directly.
// This prevents child elements from triggering dragleave and eating drops.
::v-deep thead.column-reordering th > * {
  pointer-events: none;
}

::v-deep thead th.column-dragging-source {
  opacity: 0.4;
  background-color: rgba(33, 150, 243, 0.08);
}

// Drop zone indicators — blue bar on the side the column will land.
// !important needed to override Vuetify’s .theme--dark .v-data-table th box-shadow.
::v-deep thead th.column-drop-before {
  box-shadow: inset 3px 0 0 0 #2196F3 !important;
}

::v-deep thead th.column-drop-after {
  box-shadow: inset -3px 0 0 0 #2196F3 !important;
}

// Column header content layout
::v-deep .column-header {
  .column-header-content {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 4px;
  }

  .column-drag-handle {
    cursor: grab;
    opacity: 0.34;
    transition: opacity 0.15s ease;
    flex-shrink: 0;
    min-width: 12px;
    display: flex;
    align-items: center;
    user-select: none;
    padding: 0 1px;
    background: transparent;
    border: none;
    border-radius: 2px;

    &:active {
      cursor: grabbing;
    }
  }

  &:hover .column-drag-handle {
    opacity: 0.72;
  }

  .column-header-main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    gap: 4px;

    .column-header-label {
      text-align: left;
      font-size: 0.77rem;
      font-weight: 600;
      line-height: 1.2;
    }

    .text-truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .sort-chip {
    flex-shrink: 0;
    margin-left: 2px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    padding: 0 6px;
    min-height: 18px;
    background: transparent;
    font-size: 0.68rem;
    line-height: 1;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      border-color: rgba(0, 0, 0, 0.36);
    }

    .sort-chip-content {
      display: flex;
      align-items: center;
      gap: 3px;
    }

    .sort-chip-label {
      font-weight: 500;
      letter-spacing: 0.01em;
    }

    &.sort-chip-active {
      color: #1976d2;
      border-color: rgba(25, 118, 210, 0.45);
      background: rgba(25, 118, 210, 0.08);
    }

    &.sort-chip-active:hover {
      background: rgba(25, 118, 210, 0.16);
      border-color: rgba(25, 118, 210, 0.7);
    }

    &.sort-chip-idle {
      color: rgba(0, 0, 0, 0.62);
    }
  }
}

.theme--dark ::v-deep .column-header .sort-chip {
  border-color: rgba(255, 255, 255, 0.28);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &.sort-chip-active {
    color: #64b5f6;
    border-color: rgba(100, 181, 246, 0.48);
    background: rgba(100, 181, 246, 0.14);
  }

  &.sort-chip-active:hover {
    background: rgba(100, 181, 246, 0.24);
    border-color: rgba(100, 181, 246, 0.7);
  }

  &.sort-chip-idle {
    color: rgba(255, 255, 255, 0.7);
  }
}

// Table styling with column dividers (more specific — .v-data-table)
::v-deep .v-data-table {
  min-width: 100%;

  // Column dividers for header cells - more prominent
  thead th {
    border-right: 1px solid rgba(0, 0, 0, 0.18);
    border-bottom: 2px solid rgba(0, 0, 0, 0.15);

    &:last-child {
      border-right: none;
    }
  }

  // Column dividers for body cells
  tbody td {
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    line-height: 1.35;
    vertical-align: middle;

    &:last-child {
      border-right: none;
    }

    // Neutralize Vuetify 2's default chip margin so chips sit flush inside cells
    .v-chip {
      margin: 0;
    }
  }

  // Row striping
  tbody tr {
    &:nth-of-type(odd) {
      background-color: rgba(0, 0, 0, 0.02);
    }

    &:hover {
      background-color: rgba(33, 150, 243, 0.08) !important;
      cursor: pointer;
    }

    transition: background-color 0.2s ease;
  }
}

// Dark mode table styling (most specific — .theme--dark prefix)
.theme--dark ::v-deep .v-data-table {
  // Column dividers for header cells - dark mode
  thead th {
    border-right: 1px solid rgba(255, 255, 255, 0.18);
    border-bottom: 2px solid rgba(255, 255, 255, 0.15);

    &:last-child {
      border-right: none;
    }
  }

  // Column dividers for body cells - dark mode
  tbody td {
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    line-height: 1.35;
    vertical-align: middle;

    &:last-child {
      border-right: none;
    }
  }

  // Row striping
  tbody tr {
    &:nth-of-type(odd) {
      background-color: rgba(255, 255, 255, 0.02);
    }

    &:hover {
      background-color: rgba(33, 150, 243, 0.12) !important;
    }
  }
}

// Reduce outside margins on table-group rows
.table-group {
  margin: 0 !important;

  & + .table-group {
    margin-top: 8px !important;
  }
}

.table-group-surface {
  background-color: rgb(238, 245, 250);
}

::v-deep .table-header-surface {
  background-color: #ffffff;
}

::v-deep .column-sticky-surface {
  background-color: #ffffff;
}

::v-deep .v-data-table tbody tr.table-row-context-active {
  background-color: rgba(25, 118, 210, 0.12) !important;
}

.theme--dark .table-group-surface {
  background-color: #2f2f2f;
}

.theme--dark ::v-deep .table-header-surface {
  background-color: #121212;
}

.theme--dark ::v-deep .column-sticky-surface {
  background-color: #121212;
}

.theme--dark ::v-deep .v-data-table tbody tr.table-row-context-active {
  background-color: rgba(100, 181, 246, 0.2) !important;
}
</style>
